import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import io from "socket.io-client";
import {
  fetchMessages,
  postMessage,
  messagesSelector,
  handleUpdateDataMessage,
} from "../../../service/redux/messages/messagesSlice";
import { Auth } from "../../../service/utils/auth";
import { useChat } from "../../../context/ChatContext";
import MessageBubble from "./MessageBubble";
import "./ChatWindow.css";

const socket = io("https://iudi-api.thinkdiff.us");

const ChatWindow = () => {
  const { userId: otherUserId } = useParams();
  const dispatch = useDispatch();
  const { userID } = new Auth();
  const { refreshUnread } = useChat();

  const messages = useSelector(messagesSelector);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [otherUserInfo, setOtherUserInfo] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  // Scroll to bottom khi có tin nhắn mới
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.messages.data]);

  // Load messages ban đầu
  useEffect(() => {
    if (otherUserId && userID) {
      console.log("🔄 Loading messages for:", {
        otherUserId,
        userID,
        userIDType: typeof userID,
      });
      dispatch(
        fetchMessages({
          otherUserId,
          userID,
          page: 1,
          limit: 50,
        })
      );
    }
  }, [otherUserId, userID, dispatch]);

  // Socket listener cho tin nhắn real-time + typing
  useEffect(() => {
    console.log("🔌 Setting up socket listeners for user:", otherUserId);

    const handleReceiveMessage = (data) => {
      console.log("📨 Received message from socket:", data);

      if (
        (data.idSend === parseInt(otherUserId) && data.idReceive === userID) ||
        (data.idSend === userID && data.idReceive === parseInt(otherUserId))
      ) {
        const normalized = {
          MessageID: data.MessageID ?? data.id ?? Date.now(),
          Content: data.Content ?? data.content ?? "",
          SendAt: data.SendAt ?? data.MessageTime ?? new Date().toISOString(),
          UserIDSend: data.UserIDSend ?? data.idSend,
        };
        console.log("✅ Adding message to state:", normalized);
        dispatch(handleUpdateDataMessage(normalized));
        refreshUnread();
        scrollToBottom();
      }
    };

    const handleUserTyping = (payload) => {
      console.log("⌨️ Typing indicator:", payload);
      if (
        payload?.idSend === parseInt(otherUserId) &&
        payload?.idReceive === userID
      ) {
        setIsTyping(Boolean(payload?.typing));
      }
    };

    socket.on("receive_message", handleReceiveMessage);
    socket.on("user_is_typing", handleUserTyping);

    return () => {
      console.log("🔌 Cleaning up socket listeners");
      socket.off("receive_message", handleReceiveMessage);
      socket.off("user_is_typing", handleUserTyping);
    };
  }, [otherUserId, userID, dispatch, refreshUnread]);

  // Lấy thông tin user khác từ messages.info
  useEffect(() => {
    if (messages.messages.info) {
      setOtherUserInfo({
        Username: messages.messages.info.OtherUsername || "Người dùng",
        Avatar: messages.messages.info.OtherAvatar || null,
      });
    }
  }, [messages.messages.info]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;

    const messageContent = message.trim();
    setSending(true);
    setMessage(""); // Clear input immediately for better UX

    try {
      const tempId = `temp-${Date.now()}`;
      const optimisticMessage = {
        MessageID: tempId,
        Content: messageContent,
        SendAt: new Date().toISOString(),
        UserIDSend: userID,
      };

      console.log("📤 Sending message:", messageContent);

      // Add optimistic message immediately
      dispatch(handleUpdateDataMessage(optimisticMessage));
      scrollToBottom();

      // Send via socket
      const messageData = {
        content: messageContent,
        MessageTime: new Date().toISOString(),
        idReceive: parseInt(otherUserId),
        idSend: userID,
      };

      console.log("🚀 Emitting to socket:", messageData);
      await dispatch(postMessage(messageData));

      refreshUnread();
      console.log("✅ Message sent successfully");
    } catch (error) {
      console.error("❌ Error sending message:", error);
      // Restore message on error
      setMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Nếu chưa có otherUserId, hiển thị loading
  if (!otherUserId) {
    return (
      <div className="chat-window-empty">
        <div className="empty-state">
          <div className="empty-icon">💬</div>
          <h3>Đang tải...</h3>
          <p>Vui lòng đợi</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window">
      {/* Header */}
      <div className="chat-window-header">
        <div className="header-user-info">
          <div className="header-avatar">
            {otherUserInfo?.Avatar ? (
              <img src={otherUserInfo.Avatar} alt={otherUserInfo.Username} />
            ) : (
              <div className="avatar-placeholder">
                {otherUserInfo?.Username?.charAt(0).toUpperCase() || "U"}
              </div>
            )}
          </div>
          <div className="header-user-details">
            <h3 className="header-username">{otherUserInfo?.Username}</h3>
            <span className="header-status">Đang hoạt động</span>
          </div>
        </div>
        <div className="header-actions">
          <button className="header-action-btn" title="Thêm người">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 4V16M4 10H16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button className="header-action-btn" title="Gọi video">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="2"
                y="5"
                width="12"
                height="10"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M14 8L18 5V15L14 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button className="header-action-btn" title="Cài đặt">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="10"
                cy="10"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M10 2V4M10 16V18M18 10H16M4 10H2M15.5 4.5L14 6M6 14L4.5 15.5M15.5 15.5L14 14M6 6L4.5 4.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="messages-area">
        {messages.messages.data.length === 0 ? (
          <div className="empty-messages">
            <div className="empty-icon">💬</div>
            <p>Chưa có tin nhắn nào</p>
            <p className="empty-subtitle">Hãy bắt đầu cuộc trò chuyện!</p>
          </div>
        ) : (
          <div className="messages-list">
            {messages.messages.data.map((msg, index) => {
              // Lấy sender ID từ các field có thể có
              const senderId = msg.UserIDSend || msg.SenderID || msg.idSend;

              // So sánh ĐÚNG kiểu dữ liệu - convert về number
              const isMyMessage = parseInt(senderId) === parseInt(userID);
              const showAvatar =
                index === 0 ||
                messages.messages.data[index - 1].UserIDSend !== msg.UserIDSend;

              // Debug log CHI TIẾT
              console.log(`📝 Message #${index}:`, {
                MessageID: msg.MessageID,
                Content: msg.Content?.substring(0, 20),
                senderId: senderId,
                senderIdType: typeof senderId,
                userID: userID,
                userIDType: typeof userID,
                isMyMessage: isMyMessage,
                fullMessage: msg,
              });

              return (
                <MessageBubble
                  key={msg.MessageID}
                  message={msg}
                  isMine={isMyMessage}
                  otherUserInfo={otherUserInfo}
                  showAvatar={showAvatar}
                />
              );
            })}

            {/* Typing Indicator - Hiển thị TRONG messages area như Messenger */}
            {isTyping && (
              <div className="typing-indicator-message">
                <div className="typing-indicator-avatar">
                  {otherUserInfo?.Avatar ? (
                    <img src={otherUserInfo.Avatar} alt="" />
                  ) : (
                    <div className="avatar-small-placeholder">
                      {otherUserInfo?.Username?.charAt(0).toUpperCase() || "U"}
                    </div>
                  )}
                </div>
                <div className="typing-indicator-bubble">
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="input-container">
          <button type="button" className="input-action-btn" title="Emoji">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="10"
                cy="10"
                r="8"
                stroke="currentColor"
                strokeWidth="2"
              />
              <circle cx="7" cy="8" r="1" fill="currentColor" />
              <circle cx="13" cy="8" r="1" fill="currentColor" />
              <path
                d="M6 12C6 12 7.5 14 10 14C12.5 14 14 12 14 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button type="button" className="input-action-btn" title="Mention">
            <span className="mention-icon">TO</span>
          </button>
          <textarea
            className="message-textarea"
            placeholder="Nhập tin nhắn ở đây"
            value={message}
            onChange={(e) => {
              const val = e.target.value;
              setMessage(val);
              socket.emit("start_typing", {
                idSend: userID,
                idReceive: parseInt(otherUserId),
              });
              if (typingTimeoutRef.current)
                clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = setTimeout(() => {
                socket.emit("stop_typing", {
                  idSend: userID,
                  idReceive: parseInt(otherUserId),
                });
              }, 1000);
            }}
            onKeyPress={handleKeyPress}
            rows={1}
          />
          <button type="button" className="input-action-btn" title="Đính kèm">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3L10 13M10 13L7 10M10 13L13 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 17H16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="send-message-btn"
            onClick={handleSendMessage}
            disabled={!message.trim() || sending}
            title="Gửi"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
